import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, COLLECTIONS } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { User } from '@/types/user';
import { Laboratory } from '@/types/product';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'viewer', label: 'Utilisateur' }
];

const AdminUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [laboratories, setLaboratories] = useState<Laboratory[]>([]);
  const [form, setForm] = useState<Omit<User, 'id'>>({
    email: '',
    name: '',
    role: 'viewer',
    laboratories: []
  });
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchLaboratories();
  }, []);

  const fetchUsers = async () => {
    const snapshot = await getDocs(collection(db, COLLECTIONS.USERS));
    const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as User[];
    setUsers(usersData);
  };

  const fetchLaboratories = async () => {
    const snapshot = await getDocs(collection(db, COLLECTIONS.LABORATORIES));
    const labs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Laboratory[];
    setLaboratories(labs);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLabToggle = (labId: string) => {
    setForm(prev => ({
      ...prev,
      laboratories: prev.laboratories.includes(labId)
        ? prev.laboratories.filter(id => id !== labId)
        : [...prev.laboratories, labId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    if (selectedUser) {
      await updateDoc(doc(db, COLLECTIONS.USERS, selectedUser), form);
    } else {
      await addDoc(collection(db, COLLECTIONS.USERS), form);
    }
    setForm({ email: '', name: '', role: 'viewer', laboratories: [] });
    setSelectedUser(null);
    await fetchUsers();
    setLoading(false);
  };

  const handleEdit = (user: User) => {
    setForm({
      email: user.email,
      name: user.name,
      role: user.role,
      laboratories: user.laboratories || []
    });
    setSelectedUser(user.id);
  };

  const handleDelete = async (id: string) => {
    await deleteDoc(doc(db, COLLECTIONS.USERS, id));
    await fetchUsers();
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Gestion des Utilisateurs</h1>
      <form onSubmit={handleSubmit} className="mb-8 space-y-4 bg-white p-6 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Email</Label>
            <Input name="email" value={form.email} onChange={handleInputChange} required type="email" />
          </div>
          <div>
            <Label>Nom</Label>
            <Input name="name" value={form.name} onChange={handleInputChange} required />
          </div>
        </div>
        <div>
          <Label>Rôle</Label>
          <select name="role" value={form.role} onChange={handleInputChange} className="w-full border rounded p-2">
            {ROLES.map(role => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Laboratoires accessibles</Label>
          <div className="grid grid-cols-2 gap-2">
            {laboratories.map(lab => (
              <label key={lab.id} className="flex items-center gap-2">
                <Checkbox
                  checked={form.laboratories.includes(lab.id)}
                  onCheckedChange={() => handleLabToggle(lab.id)}
                />
                <span>{lab.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-4">
          <Button type="submit" disabled={loading}>{selectedUser ? 'Mettre à jour' : 'Ajouter'}</Button>
          {selectedUser && (
            <Button type="button" variant="outline" onClick={() => { setForm({ email: '', name: '', role: 'viewer', laboratories: [] }); setSelectedUser(null); }}>Annuler</Button>
          )}
        </div>
      </form>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {users.map(user => (
          <Card key={user.id} className="overflow-hidden">
            <CardHeader>
              <CardTitle>{user.name} <span className="text-sm text-gray-500">({user.email})</span></CardTitle>
              <div className="text-sm text-blue-600 mb-2">{ROLES.find(r => r.value === user.role)?.label}</div>
            </CardHeader>
            <CardContent>
              <div className="mb-2 text-gray-700 text-sm">Laboratoires : {user.laboratories.map(labId => laboratories.find(l => l.id === labId)?.name).filter(Boolean).join(', ') || 'Aucun'}</div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => handleEdit(user)}>Modifier</Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(user.id)}>Supprimer</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminUsers; 